import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, Typography, Button, Box, TextField, Autocomplete, Stack, CircularProgress } from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';

import PersonService from 'services/person.service';
import CarrierService from 'services/carrier.service';
import { formatTaxNumber } from 'utils/formatters';
import PersonIcon from '@mui/icons-material/PersonOutline';
import BusinessIcon from '@mui/icons-material/Business';

export const useDebounce = (value: string, delay = 500) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

export const grantAccessSchema = yup.object({
  users: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        email: yup.string().email().required(),
        name: yup.string().required(),
      })
    )
    .min(1, 'Selecione ao menos um usuário')
    .required(),

  company: yup
    .object({
      id: yup.string().required(),
      cnpj: yup.string().required(),
      name: yup.string().required(),
    })
    .nullable()
    .required('Selecione uma empresa'),
});

interface UserOption {
  id: number;
  email: string;
  name: string;
}

interface CompanyOption {
  id: number;
  cnpj: string;
  name: string;
  carrierName: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onGrantAccess: (payload: { userIds: number[]; companyId: number }) => Promise<void>;
}

const personService = new PersonService();
const carrierService = new CarrierService();

const searchUsers = async (query: string): Promise<UserOption[]> => {
  if (!query) return [];

  const response = await personService.all({ emailLike: query }, 1, 10);

  return response.items.map((user) => ({
    id: user.id,
    email: user.email,
    name: user.name,
  }));
};

const GrantAccessModal = ({ open, onClose, onGrantAccess }: Props) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const [userQuery, setUserQuery] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');

  const debouncedUserQuery = useDebounce(userQuery);
  const debouncedCompanyQuery = useDebounce(companyQuery);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [allBranches, setAllBranches] = useState<CompanyOption[]>([]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const { total } = await carrierService.getCarriersRoot(1);
        const { items } = await carrierService.getCarriersRoot(1, total, { joinBranches: true });

        const branches: CompanyOption[] = [];

        items.forEach((carrier) => {
          carrier.carrierBranches?.forEach((branch) => {
            branches.push({
              id: branch.id,
              cnpj: branch.documentNumber,
              name: branch.tradeName || carrier.companyName,
              carrierName: carrier.tradeName || carrier.companyName,
            });
          });
        });

        setAllBranches(branches);
      } catch (error) {
        setAllBranches([]);
      }
    };

    fetchBranches();
  }, []);

  const searchCompanies = useCallback(
    async (query: string): Promise<CompanyOption[]> => {
      if (!query) return [];

      const cleanQuery = query.replace(/\D/g, '');

      return allBranches.filter((branch) => {
        const cleanCnpj = branch.cnpj.replace(/\D/g, '');
        const cnpjMatch = cleanQuery ? cleanCnpj.includes(cleanQuery) : false;
        const nameMatch =
          branch.name.toLowerCase().includes(query.toLowerCase()) || branch.carrierName.toLowerCase().includes(query.toLowerCase());
        return cnpjMatch || nameMatch;
      });
    },
    [allBranches]
  );

  useEffect(() => {
    const fetchUserByQuery = async () => {
      if (!debouncedUserQuery || debouncedUserQuery.length < 3) {
        setUserOptions([]);
        return;
      }

      setLoadingUsers(true);

      try {
        const users = await searchUsers(debouncedUserQuery);
        setUserOptions(users);
      } catch (error) {
        setUserOptions([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUserByQuery();
  }, [debouncedUserQuery]);

  useEffect(() => {
    const fetchCompanyByQuery = async () => {
      if (!debouncedCompanyQuery || debouncedCompanyQuery.length < 3) {
        setCompanyOptions([]);
        return;
      }

      setLoadingCompanies(true);

      try {
        const companies = await searchCompanies(debouncedCompanyQuery);
        setCompanyOptions(companies);
      } catch (error) {
        setCompanyOptions([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanyByQuery();
  }, [debouncedCompanyQuery, searchCompanies]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogContent>
        <Formik
          initialValues={{
            users: [] as UserOption[],
            company: null as CompanyOption | null,
          }}
          validationSchema={grantAccessSchema}
          onSubmit={async (values) => {
            if (!values.company?.id) return;

            await onGrantAccess({
              userIds: values.users.map((u) => u.id),
              companyId: values.company.id,
            });

            handleClose();
          }}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Stack spacing={3}>
                <Typography fontSize={20} fontWeight={600}>
                  Conceder acesso
                </Typography>

                {/* USERS */}
                <Box>
                  <Typography fontSize={16} fontWeight={500} mb={1}>
                    E-mail do usuário que terá acesso
                  </Typography>

                  <Autocomplete
                    multiple
                    options={userOptions}
                    value={values.users}
                    loading={loadingUsers}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    getOptionLabel={(option) => `${option.name} (${option.email})`}
                    onChange={(_, value) => setFieldValue('users', value)}
                    onInputChange={(_, value) => setUserQuery(value)}
                    renderTags={() => null}
                    noOptionsText={
                      debouncedUserQuery.length < 3 ? 'Digite ao menos 3 caracteres para buscar usuários' : 'Nenhum usuário encontrado'
                    }
                    loadingText="Buscando usuários..."
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        placeholder="Digite o e-mail"
                        error={Boolean(touched.users && errors.users)}
                        helperText={touched.users && errors.users ? (errors.users as string) : ''}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingUsers ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {values.users.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
                      {values.users.map((user) => (
                        <Box
                          key={user.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: 2,
                            bgcolor: '#F5F5F5',
                            border: '1px solid #E0E0E0',
                            fontSize: 14,
                            px: 1,
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            setFieldValue(
                              'users',
                              values.users.filter((u) => u.id !== user.id)
                            )
                          }
                        >
                          <PersonIcon style={{ marginRight: '6px' }} />
                          {user.name} - {user.email}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* COMPANY */}
                <Box>
                  <Typography fontSize={16} fontWeight={500} mb={1}>
                    CNPJ da empresa que será compartilhada
                  </Typography>

                  <Autocomplete
                    options={companyOptions}
                    value={values.company}
                    loading={loadingCompanies}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    getOptionLabel={(option) => formatTaxNumber(option.cnpj)}
                    onChange={(_, value) => {
                      setFieldValue('company', value);
                    }}
                    onInputChange={(_, value) => setCompanyQuery(value)}
                    noOptionsText={
                      debouncedCompanyQuery.length < 3 ? 'Digite ao menos 3 caracteres para buscar empresas' : 'Nenhuma empresa encontrada'
                    }
                    loadingText="Buscando empresas..."
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography fontSize={14} fontWeight={600}>
                            {formatTaxNumber(option.cnpj)}
                          </Typography>

                          <Typography fontSize={12} color="text.secondary">
                            {option.name}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        placeholder="Digite aqui CNPJ"
                        error={Boolean(touched.company && errors.company)}
                        helperText={touched.company && errors.company ? (errors.company as string) : ''}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCompanies ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {values.company && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 2,
                        bgcolor: '#F5F5F5',
                        border: '1px solid #E0E0E0',
                        fontSize: 14,
                        px: 1,
                        cursor: 'pointer',
                        mt: 2,
                        width: 'fit-content',
                      }}
                      onClick={() => setFieldValue('company', null)}
                    >
                      <BusinessIcon style={{ marginRight: '6px' }} />
                      {formatTaxNumber(values.company.cnpj)} - {values.company.name}
                    </Box>
                  )}
                </Box>

                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Button variant="outlined" fullWidth onClick={handleClose}>
                    Cancelar
                  </Button>

                  <Button type="submit" variant="contained" color="error" fullWidth>
                    Conceder acesso
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default GrantAccessModal;
